'use client';

import React from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { BarChart3, Coins, Info, Users } from 'lucide-react';

import { useT } from '@/hooks';

interface ReferralStatsProps {
  totalReferred: number;
  totalVolume: number;
  estimatedRewards: number;
}

/**
 * ReferralStats Component
 * Displays a grid of key performance indicators for the referral program using MUI.
 */
export const ReferralStats: React.FC<ReferralStatsProps> = ({
  totalReferred,
  totalVolume,
  estimatedRewards,
}) => {
  const { t } = useT();
  const stats = [
    {
      label: t('referral.totalReferred'),
      value: totalReferred,
      mobileValue: totalReferred.toLocaleString(undefined, { maximumFractionDigits: 1 }),
      icon: Users,
      color: 'info.main',
      bgColor: 'info.lighter',
      tooltip: t('referral.totalReferredTooltip'),
    },
    {
      label: t('referral.volumeGenerated'),
      value: `$${totalVolume.toLocaleString()}`,
      mobileValue: `$${totalVolume.toLocaleString(undefined, { maximumFractionDigits: 1 })}`,
      icon: BarChart3,
      color: 'secondary.main',
      bgColor: 'secondary.lighter',
      tooltip: t('referral.volumeGeneratedTooltip'),
    },
    {
      label: t('referral.estRewards'),
      value: `$${estimatedRewards.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
      mobileValue: `$${estimatedRewards.toLocaleString(undefined, { maximumFractionDigits: 1 })}`,
      icon: Coins,
      color: 'success.main',
      bgColor: 'success.lighter',
      tooltip: t('referral.estRewardsTooltip'),
    },
  ];

  const renderStatCard = (stat: (typeof stats)[0], index: number) => {
    return (
      <Paper
        key={stat.label}
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: { xs: 3, md: 2 },
          px: { xs: 1.5, md: 2 },
          py: { xs: 1.75, md: 2 },
          flex: 1,
          minWidth: 0,
        }}
      >
        {/* Mobile: icon on its own row, then a big value, then the label —
            three narrow cards side by side. Desktop keeps the icon-left,
            label-over-value row layout. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: { xs: 0, md: 1.5 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: 36, md: 'auto' },
              height: { xs: 36, md: 'auto' },
              p: { xs: 0, md: 1 },
              borderRadius: { xs: 1.5, md: 1 },
              bgcolor: stat.bgColor,
              color: stat.color,
              flexShrink: 0,
            }}
          >
            <stat.icon size={20} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography
              variant="h5"
              fontWeight={700}
              color="text.primary"
              fontSize={{ xs: 21, md: 30 }}
              sx={{ order: { xs: 0, md: 1 } }}
            >
              <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                {stat.mobileValue}
              </Box>
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                {stat.value}
              </Box>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, order: { xs: 1, md: 0 } }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  fontSize: { xs: 10, md: 12 },
                }}
              >
                {stat.label}
              </Typography>
              <Tooltip title={stat.tooltip} arrow placement="top">
                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    cursor: 'help',
                    color: 'text.disabled',
                    '&:hover': { color: 'text.secondary' },
                  }}
                >
                  <Info size={14} />
                </Box>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderedStats = stats.map(renderStatCard);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: { xs: 1, sm: 2 },
        mt: 3,
      }}
    >
      {renderedStats}
    </Box>
  );
};
